import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const RegistrationForm = ({ onRegister, loading = false }) => {
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    ageVerified: false,
    agreeToTerms: false,
    subscribeNewsletter: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.ageVerified) {
      newErrors.ageVerified = 'You must be at least 16 years old to use StillFlow';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        formData?.fullName
      );

      if (error) {
        setErrors({ 
          general: error?.message || 'Failed to create account. Please try again.' 
        });
        return;
      }

      if (data?.user) {
        if (onRegister) {
          onRegister({
            email: formData?.email,
            fullName: formData?.fullName,
            subscribeNewsletter: formData?.subscribeNewsletter
          });
        }
      }
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General Error Message */}
      {errors?.general && (
        <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg p-4 animate-fade-in">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-white flex-shrink-0" />
            <p className="font-body text-sm text-white drop-shadow">{errors?.general}</p>
          </div>
        </div>
      )}

      {/* Full Name Field */}
      <div>
        <label className="block font-body text-sm font-medium text-white/90 mb-2 drop-shadow">
          Full Name
        </label>
        <Input
          type="text"
          name="fullName"
          value={formData?.fullName}
          onChange={handleInputChange}
          placeholder="Enter your full name"
          error={errors?.fullName}
          required
          disabled={isSubmitting || loading}
          className="w-full bg-white/10 border-white/30 text-white placeholder-white/50 backdrop-blur-sm focus:bg-white/20 focus:border-white/50"
        />
        {errors?.fullName && (
          <p className="mt-1 font-caption text-xs text-white drop-shadow">{errors?.fullName}</p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label className="block font-body text-sm font-medium text-white/90 mb-2 drop-shadow">
          Email Address
        </label>
        <Input
          type="email"
          name="email"
          value={formData?.email}
          onChange={handleInputChange}
          placeholder="Enter your email address"
          error={errors?.email}
          required
          disabled={isSubmitting || loading}
          className="w-full bg-white/10 border-white/30 text-white placeholder-white/50 backdrop-blur-sm focus:bg-white/20 focus:border-white/50"
        />
        {errors?.email && (
          <p className="mt-1 font-caption text-xs text-white drop-shadow">{errors?.email}</p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label className="block font-body text-sm font-medium text-white/90 mb-2 drop-shadow">
          Password
        </label>
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData?.password}
            onChange={handleInputChange}
            placeholder="Create a strong password"
            error={errors?.password}
            required
            disabled={isSubmitting || loading}
            className="w-full pr-12 bg-white/10 border-white/30 text-white placeholder-white/50 backdrop-blur-sm focus:bg-white/20 focus:border-white/50"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            disabled={isSubmitting || loading}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>
        {errors?.password && (
          <p className="mt-1 font-caption text-xs text-white drop-shadow">{errors?.password}</p>
        )}
      </div>

      {/* Confirm Password Field */}
      <div>
        <label className="block font-body text-sm font-medium text-white/90 mb-2 drop-shadow">
          Confirm Password
        </label>
        <div className="relative">
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            placeholder="Re-enter your password"
            error={errors?.confirmPassword}
            required
            disabled={isSubmitting || loading}
            className="w-full pr-12 bg-white/10 border-white/30 text-white placeholder-white/50 backdrop-blur-sm focus:bg-white/20 focus:border-white/50"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            disabled={isSubmitting || loading}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>
        {errors?.confirmPassword && (
          <p className="mt-1 font-caption text-xs text-white drop-shadow">{errors?.confirmPassword}</p>
        )}
      </div>

      {/* Terms Checkbox */}
      <div className="space-y-3">
        {/* Age Verification Checkbox - NEW */}
        <div>
          <div className="flex items-start space-x-3">
            <Checkbox
              name="ageVerified"
              checked={formData?.ageVerified}
              onChange={handleInputChange}
              disabled={isSubmitting || loading}
              className="mt-0.5"
            />
            <label className="font-body text-sm text-white/90 drop-shadow">
              I am 16 years of age or older
            </label>
          </div>
          {errors?.ageVerified && (
            <p className="mt-1 font-caption text-xs text-white drop-shadow ml-7">{errors?.ageVerified}</p>
          )}
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            name="agreeToTerms"
            checked={formData?.agreeToTerms}
            onChange={handleInputChange}
            disabled={isSubmitting || loading}
            className="mt-0.5"
          />
          <label className="font-body text-sm text-white/90 drop-shadow">
            I agree to the{' '}
            <button type="button" className="text-white font-semibold hover:text-white/80 transition-colors underline">
              Terms of Service
            </button>{' '}
            and{' '}
            <Link to="/privacy-policy" className="text-white font-semibold hover:text-white/80 transition-colors underline">
              Privacy Policy
            </Link>
          </label>
        </div>
        {errors?.agreeToTerms && (
          <p className="font-caption text-xs text-white drop-shadow">{errors?.agreeToTerms}</p>
        )}

        <div className="flex items-start space-x-3">
          <Checkbox
            name="subscribeNewsletter"
            checked={formData?.subscribeNewsletter}
            onChange={handleInputChange}
            disabled={isSubmitting || loading}
            className="mt-0.5"
          />
          <label className="font-body text-sm text-white/90 drop-shadow">
            Subscribe to newsletter for planner tips and updates
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isSubmitting || loading}
        disabled={isSubmitting || loading}
        iconName="UserPlus"
        iconPosition="left"
        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      >
        {isSubmitting ? 'Creating Account...' : 'Create Account'}
      </Button>

      {/* Email Verification Notice */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Icon name="Mail" size={16} className="text-white flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-body text-sm text-white font-medium mb-1 drop-shadow">Email Verification</p>
            <p className="font-caption text-xs text-white/80 drop-shadow">
              After signing up, check your email for a verification link to activate your account.
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default RegistrationForm;